import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Truck,
  Package,
  CheckCircle2,
  Clock,
  Search,
  X,
  UserCheck,
  User,
  Factory,
  Warehouse,
  ArrowRight,
  RotateCcw,
  FileText,
  Phone,
  AlertCircle,
  MapPin,
  Calendar,
  Edit2,
  Download,
  Send,
  Check,
} from "lucide-react";
import { toast } from "sonner";
import Pagination from "./Pagination";
import { api, downloadDocument } from "../../lib/api";
import { compactMoney, money } from "../../lib/format";
import { isAdmin, canAccessDepartment, canViewClientPhone } from "../../lib/permissions";
import { useSavedState } from "../../lib/listState";

// -------------------------------------------------------------
// Helper: Evaluate Order Dispatch Stage & Next Action
// -------------------------------------------------------------
export function getOrderDispatchInfo(order, optimisticStatus = {}) {
  const opt = optimisticStatus[order.order_id] || {};
  const o = { ...order, ...opt };

  const isDel =
    o.delivery_status === "Delivered" ||
    o.dispatch_status === "Delivered" ||
    o.current_stage === "Delivered";

  const isOut =
    !isDel &&
    (o.dispatch_status === "Out for Delivery" ||
      o.delivery_status === "Out for Delivery");

  const hasStaff = Boolean(o.assigned_delivery_person);
  const hasChallan = Boolean(
    o.challan_number || o.challan_id || o.challan_document_id
  );

  let statusLabel = "Ready for Dispatch";
  let statusBadge = "amber";
  let stageKey = "ready"; // 'ready' | 'assigned' | 'out' | 'delivered'

  if (isDel) {
    statusLabel = "Delivered";
    statusBadge = "green";
    stageKey = "delivered";
  } else if (isOut) {
    statusLabel = "Out for Delivery";
    statusBadge = "purple";
    stageKey = "out";
  } else if (hasStaff) {
    statusLabel = "Delivery Assigned";
    statusBadge = "blue";
    stageKey = "assigned";
  } else {
    statusLabel = "Ready for Dispatch";
    statusBadge = "amber";
    stageKey = "ready";
  }

  return {
    order: o,
    isDel,
    isOut,
    hasStaff,
    hasChallan,
    statusLabel,
    statusBadge,
    stageKey,
  };
}

export default function Dispatch({ orders, onOpen, user, onChange, routeParams }) {
  const [search, setSearch] = useSavedState("dispatch_main", "search", "");
  const [statusFilter, setStatusFilter] = useSavedState("dispatch_main", "statusFilter", routeParams?.statusFilter || "");
  const [companyFilter, setCompanyFilter] = useSavedState(
    "dispatch_main",
    "company",
    routeParams?.company && routeParams.company !== "All Companies" ? routeParams.company : ""
  );
  const [priorityFilter, setPriorityFilter] = useSavedState("dispatch_main", "priorityFilter", "");
  const [vendorFilter, setVendorFilter] = useSavedState("dispatch_main", "vendorFilter", "");
  const [assignedUserFilter, setAssignedUserFilter] = useSavedState("dispatch_main", "assignedUserFilter", "");
  const [deliveryPersonFilter, setDeliveryPersonFilter] = useSavedState("dispatch_main", "deliveryPersonFilter", "");

  useEffect(() => {
    if (routeParams?.statusFilter !== undefined) {
      setStatusFilter(routeParams.statusFilter || "");
    }
    if (routeParams?.company !== undefined) {
      setCompanyFilter(routeParams.company !== "All Companies" ? routeParams.company : "");
    }
  }, [routeParams?.statusFilter, routeParams?.company, setStatusFilter, setCompanyFilter]);

  // Resource lists
  const [deliveryAgents, setDeliveryAgents] = useState([]);
  const [activeUsers, setActiveUsers] = useState([]);
  const [vendors, setVendors] = useState([]);

  // Optimistic instantaneous state updates & processing guards
  const [optimisticStatus, setOptimisticStatus] = useState({});
  const [busyIds, setBusyIds] = useState(new Set());

  // Action Modals State
  const [assignModalData, setAssignModalData] = useState(null);
  const [challanModalData, setChallanModalData] = useState(null);
  const [outModalData, setOutModalData] = useState(null);

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const canAct = isAdmin(user) || canAccessDepartment(user, ["Production", "Delivery", "Sales"]);
  const canSeePhone = canViewClientPhone(user);


  // Dynamic list of active CRM users authorized for production / dispatch responsibilities
  const assignedUserOptions = useMemo(() => {
    const userMap = new Map();
    // 1. Add active eligible users from API
    activeUsers.forEach((u) => {
      if (u.name) {
        userMap.set(u.name.toLowerCase().trim(), {
          name: u.name,
          user_id: u.user_id,
          role: u.role || u.department || "Staff",
        });
      }
    });
    // 2. Add any assigned users already present on current dispatch orders
    (orders || []).forEach((o) => {
      const pUser = o.assigned_producer || o.assigned_production_user;
      if (pUser && !userMap.has(pUser.toLowerCase().trim())) {
        userMap.set(pUser.toLowerCase().trim(), {
          name: pUser,
          user_id: o.producer_id || o.production_user_id || "",
          role: "Production",
        });
      }
      const dUser = o.assigned_delivery_person;
      if (dUser && !userMap.has(dUser.toLowerCase().trim())) {
        userMap.set(dUser.toLowerCase().trim(), {
          name: dUser,
          user_id: o.delivery_person_id || "",
          role: "Delivery",
        });
      }
    });
    return Array.from(userMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [activeUsers, orders]);

  // Fetch Delivery Staff & Vendors
  const loadResources = useCallback(() => {
    api("get", "/users?department=Delivery&active_only=true")
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : [];
        const validAgents = list.filter(
          (u) =>
            !u.is_deleted &&
            u.is_active !== false &&
            ((u.department || "").toLowerCase() === "delivery" || (u.role || "").toLowerCase() === "delivery")
        );

        // Also ensure any delivery person assigned on existing orders is included with consistent eligibility rules
        const agentMap = new Map();
        validAgents.forEach((a) => {
          const key = (a.name || "").trim().toLowerCase();
          if (key) {
            agentMap.set(key, {
              ...a,
              user_id: a.user_id || a.id || key,
              name: a.name,
              phone: a.phone || "",
            });
          }
        });

        if (Array.isArray(orders)) {
          orders.forEach((o) => {
            const pName = (o.assigned_delivery_person || "").trim();
            if (pName) {
              const key = pName.toLowerCase();
              if (!agentMap.has(key)) {
                agentMap.set(key, {
                  user_id: o.delivery_person_id || `DEL-${key.replace(/[^a-z0-9]+/g, "-").toUpperCase()}`,
                  name: pName,
                  phone: o.delivery_person_phone || "",
                  department: "Delivery",
                  role: "user",
                  is_active: true,
                });
              } else if (!agentMap.get(key).phone && o.delivery_person_phone) {
                agentMap.get(key).phone = o.delivery_person_phone;
              }
            }
          });
        }

        setDeliveryAgents(Array.from(agentMap.values()));
      })
      .catch(() => {});

    api("get", "/vendors?status=Active")
      .then((r) => setVendors(r.data || []))
      .catch(() => {});
  }, [orders]);

  useEffect(() => {
    loadResources();
  }, [loadResources]);

  // Busy lock helpers
  const setOrderBusy = (idList, isBusy) => {
    setBusyIds((prev) => {
      const next = new Set(prev);
      idList.forEach((id) => {
        if (isBusy) next.add(id);
        else next.delete(id);
      });
      return next;
    });
  };

  // Optimistic state updater
  const updateOptimistic = (orderIds, updates) => {
    setOptimisticStatus((prev) => {
      const next = { ...prev };
      orderIds.forEach((id) => {
        next[id] = { ...(next[id] || {}), ...updates };
      });
      return next;
    });
  };

  // Filter raw orders that belong in the Dispatch workflow
  const rawDispatchOrders = useMemo(() => {
    return (orders || []).filter((o) => {
      if (o.deleted) return false;
      const opt = optimisticStatus[o.order_id] || {};
      const merged = { ...o, ...opt };

      // Order must be actively in Dispatch stage
      if (merged.current_stage !== "Dispatch") return false;

      // Filter by company if scoped
      if (companyFilter) {
        const oComp = (merged.company || "").toLowerCase();
        if (oComp && oComp !== companyFilter.toLowerCase()) return false;
      }

      // Immediately exclude orders that have been handed over / sent Out for Delivery / Delivered
      if (
        merged.delivery_status === "Out for Delivery" ||
        merged.delivery_status === "Delivered" ||
        merged.dispatch_status === "Out for Delivery" ||
        merged.dispatch_status === "Delivered" ||
        merged.dispatch_status === "Dispatched" ||
        merged.handed_to_delivery ||
        merged.out_for_delivery_at ||
        merged.delivered_at
      ) {
        return false;
      }

      return true;
    });
  }, [orders, optimisticStatus, companyFilter]);

  // Group into Single Orders and Bulk Batches
  const groupedDispatchItems = useMemo(() => {
    const batchMap = new Map();
    const singles = [];

    rawDispatchOrders.forEach((rawOrder) => {
      const opt = optimisticStatus[rawOrder.order_id] || {};
      const o = { ...rawOrder, ...opt };
      const batchId = o.upload_batch_id;
      if (batchId) {
        if (!batchMap.has(batchId)) {
          batchMap.set(batchId, []);
        }
        batchMap.get(batchId).push(o);
      } else {
        singles.push(o);
      }
    });

    const items = [...singles];

    batchMap.forEach((batchOrders, batchId) => {
      const totalInBatch = (orders || []).filter((o) => o.upload_batch_id === batchId && !o.deleted).length;
      if (batchOrders.length === 1 && totalInBatch <= 1 && !batchOrders[0].is_bulk_upload) {
        items.push(batchOrders[0]);
        return;
      }

      const first = batchOrders[0];

      // Itemized counts
      let totalAssigned = 0;
      let totalUnassigned = 0;

      batchOrders.forEach((bo) => {
        const info = getOrderDispatchInfo(bo, optimisticStatus);
        if (info.hasStaff) totalAssigned += 1;
        else totalUnassigned += 1;
      });

      const counts = {
        total: batchOrders.length,
        deliveryAssigned: totalAssigned,
        needsStaff: totalUnassigned,
        ready: batchOrders.length,
      };

      let overallStatus = "Ready for Dispatch";
      if (totalAssigned === batchOrders.length && batchOrders.length > 0) {
        overallStatus = "Delivery Assigned";
      } else if (totalAssigned > 0) {
        overallStatus = "Partially Assigned";
      } else {
        overallStatus = "Ready for Dispatch";
      }

      const priority = batchOrders.some((o) => o.priority === "Urgent")
        ? "Urgent"
        : batchOrders.some((o) => o.priority === "High")
        ? "High"
        : "Normal";

      const assignedPerson = batchOrders.find((o) => o.assigned_delivery_person)?.assigned_delivery_person || "";
      const deliveryPhone = batchOrders.find((o) => o.delivery_person_phone)?.delivery_person_phone || "";
      const vendorName = batchOrders.find((o) => o.production_vendor_name)?.production_vendor_name || "";
      const producerName =
        batchOrders.find((o) => o.assigned_producer || o.assigned_production_user)?.assigned_producer ||
        batchOrders.find((o) => o.assigned_producer || o.assigned_production_user)?.assigned_production_user ||
        "";
      const warehouseName = batchOrders.find((o) => o.warehouse_name)?.warehouse_name || "";
      const challanNum = batchOrders.find((o) => o.challan_number)?.challan_number || "";

      items.push({
        isBatch: true,
        batch_id: batchId,
        batch_name: first.upload_batch_name || `Bulk Dispatch Request · ${batchOrders.length} Products`,
        client_name: first.client_name,
        client_id: first.client_id,
        client_contact_person: first.client_contact_person,
        client_phone: first.client_phone,
        client_email: first.client_email,
        priority,
        total_quantity: batchOrders.reduce((sum, o) => sum + (o.quantity || 0), 0),
        total_value: batchOrders.reduce((sum, o) => sum + (o.total_value || 0), 0),
        orders: batchOrders,
        counts,
        dispatch_status: overallStatus,
        assigned_delivery_person: assignedPerson,
        delivery_person_phone: deliveryPhone,
        production_vendor_name: vendorName,
        assigned_producer: producerName,
        warehouse_name: warehouseName,
        challan_number: challanNum,
        ready_for_dispatch_at: batchOrders.find((o) => o.ready_for_dispatch_at)?.ready_for_dispatch_at || first.updated_at,
        created_at: first.created_at,
        updated_at: first.updated_at,
      });
    });

    // Sort newest first
    items.sort((a, b) => {
      const getT = (item) => {
        if (item.isBatch) {
          const times = (item.orders || []).map((o) => {
            const t = o.ready_for_dispatch_at || o.production_actual || o.updated_at || o.created_at || o.order_date;
            return t ? new Date(t).getTime() : 0;
          });
          return times.length ? Math.max(...times) : 0;
        }
        const t = item.ready_for_dispatch_at || item.production_actual || item.updated_at || item.created_at || item.order_date;
        return t ? new Date(t).getTime() : 0;
      };
      return getT(b) - getT(a);
    });

    return items;
  }, [rawDispatchOrders, optimisticStatus, orders]);

  // Summary Metrics treating 1 Bulk Request as 1 unit
  const dispatchMetrics = useMemo(() => {
    let needsStaff = 0;
    let readyToSendOut = 0;

    groupedDispatchItems.forEach((item) => {
      if (item.isBatch) {
        const hasUnassigned = item.orders.some((o) => !o.assigned_delivery_person);
        if (hasUnassigned) {
          needsStaff += 1;
        } else {
          readyToSendOut += 1;
        }
      } else {
        const info = getOrderDispatchInfo(item, optimisticStatus);
        if (!info.hasStaff) {
          needsStaff += 1;
        } else {
          readyToSendOut += 1;
        }
      }
    });

    return {
      total: groupedDispatchItems.length,
      needsStaff,
      readyToSendOut,
    };
  }, [groupedDispatchItems, optimisticStatus]);

  // Filtered items based on search and toolbar filters
  const filteredDispatchItems = useMemo(() => {
    return groupedDispatchItems.filter((item) => {
      if (item.isBatch) {
        if (statusFilter) {
          if (statusFilter === "Needs Staff" && !item.orders.some((o) => !o.assigned_delivery_person)) return false;
          if (
            (statusFilter === "Ready to Send Out" || statusFilter === "Ready for Delivery") &&
            !item.orders.some((o) => o.assigned_delivery_person)
          ) {
            return false;
          }
        }
        if (priorityFilter && item.priority !== priorityFilter) return false;
        if (vendorFilter) {
          if (vendorFilter === "unassigned") {
            if (item.production_vendor_name) return false;
          } else if (!item.production_vendor_name?.toLowerCase().includes(vendorFilter.toLowerCase())) {
            return false;
          }
        }
        if (assignedUserFilter) {
          if (assignedUserFilter === "unassigned") {
            // Unassigned: has orders with neither production user nor delivery staff assigned
            const hasUnassigned = item.orders.some(
              (o) => !(o.assigned_producer || o.assigned_production_user) && !o.assigned_delivery_person
            );
            if (!hasUnassigned) return false;
          } else {
            const targetUser = assignedUserFilter.toLowerCase().trim();
            const hasUser =
              item.assigned_producer?.toLowerCase().trim() === targetUser ||
              item.assigned_delivery_person?.toLowerCase().trim() === targetUser ||
              item.orders.some((o) => {
                const prod = (o.assigned_producer || o.assigned_production_user || "").toLowerCase().trim();
                const deliv = (o.assigned_delivery_person || "").toLowerCase().trim();
                return prod === targetUser || deliv === targetUser;
              });
            if (!hasUser) return false;
          }
        }
        if (deliveryPersonFilter) {
          if (deliveryPersonFilter === "unassigned") {
            if (item.assigned_delivery_person) return false;
          } else if (!item.assigned_delivery_person?.toLowerCase().includes(deliveryPersonFilter.toLowerCase())) {
            return false;
          }
        }
        if (search) {
          const q = search.toLowerCase();
          const match =
            item.batch_id.toLowerCase().includes(q) ||
            item.batch_name.toLowerCase().includes(q) ||
            item.client_name.toLowerCase().includes(q) ||
            item.client_contact_person?.toLowerCase().includes(q) ||
            item.production_vendor_name?.toLowerCase().includes(q) ||
            item.warehouse_name?.toLowerCase().includes(q) ||
            item.assigned_delivery_person?.toLowerCase().includes(q) ||
            item.challan_number?.toLowerCase().includes(q) ||
            item.orders.some(
              (o) =>
                o.order_id?.toLowerCase().includes(q) ||
                o.product?.toLowerCase().includes(q) ||
                o.product_code?.toLowerCase().includes(q) ||
                o.po_number?.toLowerCase().includes(q)
            );
          if (!match) return false;
        }
        return true;
      }

      // Single Order Filter
      const info = getOrderDispatchInfo(item, optimisticStatus);

      if (statusFilter) {
        if (statusFilter === "Needs Staff" && info.hasStaff) return false;
        if (
          (statusFilter === "Ready to Send Out" || statusFilter === "Ready for Delivery") &&
          !info.hasStaff
        ) {
          return false;
        }
      }
      if (priorityFilter && item.priority !== priorityFilter) return false;
      if (vendorFilter) {
        const v = item.production_vendor_name || "";
        if (vendorFilter === "unassigned") {
          if (v) return false;
        } else if (!v.toLowerCase().includes(vendorFilter.toLowerCase())) {
          return false;
        }
      }
      if (assignedUserFilter) {
        const prodUser = (item.assigned_producer || item.assigned_production_user || "").toLowerCase().trim();
        const deliveryUser = (item.assigned_delivery_person || "").toLowerCase().trim();
        if (assignedUserFilter === "unassigned") {
          // Unassigned: neither production user nor delivery staff
          if (prodUser || deliveryUser) return false;
        } else {
          const targetUser = assignedUserFilter.toLowerCase().trim();
          if (prodUser !== targetUser && deliveryUser !== targetUser) return false;
        }
      }
      if (deliveryPersonFilter) {
        const dp = item.assigned_delivery_person || "";
        if (deliveryPersonFilter === "unassigned") {
          if (dp) return false;
        } else if (!dp.toLowerCase().includes(deliveryPersonFilter.toLowerCase())) {
          return false;
        }
      }
      if (search) {
        const q = search.toLowerCase();
        const match =
          item.order_id?.toLowerCase().includes(q) ||
          item.client_name?.toLowerCase().includes(q) ||
          item.client_contact_person?.toLowerCase().includes(q) ||
          item.product?.toLowerCase().includes(q) ||
          item.product_code?.toLowerCase().includes(q) ||
          item.production_vendor_name?.toLowerCase().includes(q) ||
          item.warehouse_name?.toLowerCase().includes(q) ||
          item.assigned_delivery_person?.toLowerCase().includes(q) ||
          item.challan_number?.toLowerCase().includes(q) ||
          item.tracking_number?.toLowerCase().includes(q) ||
          item.po_number?.toLowerCase().includes(q);
        if (!match) return false;
      }
      return true;
    });
  }, [groupedDispatchItems, statusFilter, priorityFilter, vendorFilter, assignedUserFilter, deliveryPersonFilter, search, optimisticStatus]);

  // Paginated items
  const paginatedDispatchItems = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredDispatchItems.slice(start, start + pageSize);
  }, [filteredDispatchItems, page, pageSize]);

  // ==============================================================
  // Action Handlers
  // ==============================================================

  // 1. Assign Delivery Person
  const handleConfirmAssign = async ({ mode, order, batch, targetOrders, agentName, agentPhone, agentId, remarks }) => {
    const targetIds = targetOrders.map((o) => o.order_id);
    setOrderBusy(targetIds, true);

    try {
      if (mode === "single" || targetIds.length === 1) {
        const singleId = targetIds[0];
        await api("patch", `/orders/${singleId}/assign-delivery`, {
          assigned_delivery_person: agentName,
          delivery_person_phone: agentPhone || "",
          delivery_person_id: agentId || "",
          remarks: remarks || "",
        });
        toast.success(`Assigned ${agentName} to Order #${singleId}`);
      } else {
        await api("patch", `/orders/batch/${batch.batch_id}/assign-delivery`, {
          assigned_delivery_person: agentName,
          delivery_person_phone: agentPhone || "",
          delivery_person_id: agentId || "",
          remarks: remarks || `Bulk batch assigned to ${agentName}`,
          order_ids: targetIds,
        });
        toast.success(`Assigned ${agentName} to ${targetIds.length} products in #${batch.batch_id}`);
      }

      // Optimistic update
      updateOptimistic(targetIds, {
        assigned_delivery_person: agentName,
        delivery_person_phone: agentPhone || "",
        delivery_person_id: agentId || "",
        current_stage: "Dispatch",
        dispatch_status: "Ready for Dispatch",
        delivery_status: "Pending",
      });

      setAssignModalData(null);
      onChange && onChange();
    } catch (err) {
      console.error("Assign Delivery Staff Error:", err);
      const msg = err.response?.data?.detail || "Failed to assign delivery person";
      toast.error(typeof msg === "string" ? msg : "Assignment failed");
    } finally {
      setOrderBusy(targetIds, false);
    }
  };

  // 2. Direct Out for Delivery Actions (Single & Batch)
  const handleDirectOutForDelivery = async (targetOrder) => {
    const targetId = targetOrder.order_id;
    setOrderBusy([targetId], true);

    try {
      await api("post", `/orders/${targetId}/out-for-delivery`, {
        transporter: targetOrder.assigned_delivery_person || "In-House Delivery",
        tracking_number: targetOrder.tracking_number || "",
        remarks: targetOrder.dispatch_remarks || "",
      });
      toast.success(`Order #${targetId} sent Out for Delivery!`);

      // Optimistic update
      updateOptimistic([targetId], {
        dispatch_status: "Out for Delivery",
        delivery_status: "Out for Delivery",
        current_stage: "Delivery",
        handed_to_delivery: true,
        out_for_delivery_at: new Date().toISOString(),
        transporter: targetOrder.assigned_delivery_person || "In-House Delivery",
      });

      onChange && onChange();
    } catch (err) {
      console.error("Direct Out for Delivery Error:", err);
      const msg = err.response?.data?.detail || "Could not mark order out for delivery";
      toast.error(typeof msg === "string" ? msg : "Action failed");
    } finally {
      setOrderBusy([targetId], false);
    }
  };

  const handleBatchDirectOutForDelivery = async (batch, targetOrders) => {
    const targetIds = targetOrders.map((o) => o.order_id);
    setOrderBusy(targetIds, true);

    try {
      const defaultTransporter =
        batch.assigned_delivery_person ||
        targetOrders.find((o) => o.assigned_delivery_person)?.assigned_delivery_person ||
        "In-House Delivery";

      await api("post", `/orders/batch/${batch.batch_id}/out-for-delivery`, {
        transporter: defaultTransporter,
        tracking_number: batch.tracking_number || "",
        remarks: "Bulk batch out for delivery",
        order_ids: targetIds,
      });

      toast.success(`${targetIds.length} product(s) in #${batch.batch_id} marked Out for Delivery!`);

      // Optimistic update
      updateOptimistic(targetIds, {
        dispatch_status: "Out for Delivery",
        delivery_status: "Out for Delivery",
        current_stage: "Delivery",
        handed_to_delivery: true,
        out_for_delivery_at: new Date().toISOString(),
        transporter: defaultTransporter,
      });

      onChange && onChange();
    } catch (err) {
      console.error("Batch Direct Out for Delivery Error:", err);
      const msg = err.response?.data?.detail || "Could not mark batch out for delivery";
      toast.error(typeof msg === "string" ? msg : "Action failed");
    } finally {
      setOrderBusy(targetIds, false);
    }
  };

  // 3. Send Out for Delivery with Modal (if needed for custom details)
  const handleConfirmOutForDelivery = async ({ mode, order, batch, targetOrders, transporter, trackingNumber, remarks }) => {
    const targetIds = targetOrders.map((o) => o.order_id);
    setOrderBusy(targetIds, true);

    try {
      if (mode === "single" || targetIds.length === 1) {
        const singleId = targetIds[0];
        const singleOrder = targetOrders[0];
        await api("post", `/orders/${singleId}/out-for-delivery`, {
          transporter: transporter || singleOrder.assigned_delivery_person || "In-House Delivery",
          tracking_number: trackingNumber || "",
          remarks: remarks || "",
        });
        toast.success(`Order #${singleId} sent Out for Delivery!`);
      } else {
        await api("post", `/orders/batch/${batch.batch_id}/out-for-delivery`, {
          transporter: transporter || batch.assigned_delivery_person || "In-House Delivery",
          tracking_number: trackingNumber || "",
          remarks: remarks || "Bulk batch out for delivery",
          order_ids: targetIds,
        });
        toast.success(`${targetIds.length} products in #${batch.batch_id} marked Out for Delivery!`);
      }

      // Optimistic update: mark as handed over and move to Delivery
      updateOptimistic(targetIds, {
        dispatch_status: "Out for Delivery",
        delivery_status: "Out for Delivery",
        current_stage: "Delivery",
        handed_to_delivery: true,
        out_for_delivery_at: new Date().toISOString(),
        transporter: transporter || "In-House Delivery",
        tracking_number: trackingNumber || "",
        dispatch_remarks: remarks || "",
      });

      setOutModalData(null);
      onChange && onChange();
    } catch (err) {
      console.error("Out for Delivery Error:", err);
      const msg = err.response?.data?.detail || "Could not mark order out for delivery";
      toast.error(typeof msg === "string" ? msg : "Action failed");
    } finally {
      setOrderBusy(targetIds, false);
    }
  };

  return (
    <div className="content">
      {/* Page Heading */}
      <div className="page-heading">
        <div>
          <div className="small-label">QUEUE</div>
          <h1>Dispatch</h1>
          <p className="muted">Production-completed orders and bulk requests ready to ship.</p>
        </div>
      </div>

      {/* 1. SUMMARY METRICS */}
      <div className="metrics-grid" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
        <MetricBox
          label="Ready for Dispatch"
          value={dispatchMetrics.total}
          tone="blue"
          active={statusFilter === ""}
          onClick={() => {
            setStatusFilter("");
            setPage(1);
          }}
        />
        <MetricBox
          label="Needs Delivery Person"
          value={dispatchMetrics.needsStaff}
          tone="amber"
          active={statusFilter === "Needs Staff"}
          onClick={() => {
            setStatusFilter(statusFilter === "Needs Staff" ? "" : "Needs Staff");
            setPage(1);
          }}
        />
        <MetricBox
          label="Ready to Send Out"
          value={dispatchMetrics.readyToSendOut}
          tone="green"
          active={statusFilter === "Ready to Send Out" || statusFilter === "Ready for Delivery"}
          onClick={() => {
            setStatusFilter(
              statusFilter === "Ready to Send Out" || statusFilter === "Ready for Delivery"
                ? ""
                : "Ready to Send Out"
            );
            setPage(1);
          }}
        />
      </div>

      {/* Active Filter Banner */}
      {(statusFilter || companyFilter) && (
        <div className="active-filter-banner" style={{ marginTop: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 13, color: "var(--navy)", fontWeight: 600 }}>Active Filters:</span>
            {statusFilter && (
              <span className="badge amber" style={{ fontWeight: 700 }} data-testid="active-filter-dispatch-status">
                Status: {statusFilter === "Needs Staff" ? "Needs Delivery Person" : (statusFilter === "Ready to Send Out" || statusFilter === "Ready for Delivery") ? "Ready to Send Out" : statusFilter}
              </span>
            )}
            {companyFilter && (
              <span className="badge purple" style={{ fontWeight: 700 }} data-testid="active-filter-dispatch-company">
                Company: {companyFilter}
              </span>
            )}
            <span className="badge gray" style={{ fontWeight: 600 }}>
              {filteredDispatchItems.length} matching {filteredDispatchItems.length === 1 ? "item" : "items"}
            </span>
          </div>
          <button
            type="button"
            className="clear-btn"
            onClick={() => {
              setStatusFilter("");
              setCompanyFilter("");
              setPage(1);
            }}
            data-testid="clear-dispatch-filters-btn"
          >
            Clear filter
          </button>
        </div>
      )}

      {/* 2. SEARCH & FILTERS TOOLBAR */}
      <div className="toolbar" style={{ marginTop: 14, marginBottom: 16 }}>
        <div className="search-box">
          <Search size={16} />
          <input
            type="text"
            placeholder="Search Order ID, Batch ID, Client, Product, Vendor, Delivery Person…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            data-testid="dispatch-search-input"
          />
          {search && (
            <button
              type="button"
              className="icon-btn"
              onClick={() => {
                setSearch("");
                setPage(1);
              }}
              style={{ padding: 2, background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center" }}
              title="Clear search"
            >
              <X size={14} />
            </button>
          )}
        </div>

        {/* Status filter */}
        <select
          value={statusFilter === "Ready for Delivery" ? "Ready to Send Out" : statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
          data-testid="dispatch-status-filter"
        >
          <option value="">All Statuses</option>
          <option value="Needs Staff">Needs Delivery Person</option>
          <option value="Ready to Send Out">Ready to Send Out</option>
        </select>

        {/* Priority filter */}
        <select
          value={priorityFilter}
          onChange={(e) => {
            setPriorityFilter(e.target.value);
            setPage(1);
          }}
          data-testid="dispatch-priority-filter"
        >
          <option value="">All Priorities</option>
          <option value="Urgent">Urgent</option>
          <option value="High">High</option>
          <option value="Normal">Normal</option>
        </select>

        {/* Assigned User filter */}
        <select
          value={assignedUserFilter}
          onChange={(e) => {
            setAssignedUserFilter(e.target.value);
            setPage(1);
          }}
          data-testid="dispatch-assigned-user-filter"
        >
          <option value="">All Users</option>
          <option value="unassigned">Unassigned</option>
          {assignedUserOptions.map((u) => (
            <option key={u.user_id || u.name} value={u.name}>
              {u.name}
            </option>
          ))}
        </select>

        {/* Delivery Person filter */}
        <select
          value={deliveryPersonFilter}
          onChange={(e) => {
            setDeliveryPersonFilter(e.target.value);
            setPage(1);
          }}
          data-testid="dispatch-delivery-person-filter"
        >
          <option value="">All Delivery Staff</option>
          <option value="unassigned">Unassigned</option>
          {deliveryAgents.map((a) => (
            <option key={a.user_id} value={a.name}>
              {a.name}
            </option>
          ))}
        </select>

        {(search || statusFilter || priorityFilter || assignedUserFilter || vendorFilter || deliveryPersonFilter) && (
          <button
            type="button"
            className="outline-btn compact"
            onClick={() => {
              setSearch("");
              setStatusFilter("");
              setPriorityFilter("");
              setAssignedUserFilter("");
              setVendorFilter("");
              setDeliveryPersonFilter("");
              setPage(1);
            }}
            style={{ fontSize: 12 }}
            title="Reset all filters"
          >
            Reset Filters
          </button>
        )}
      </div>

      {/* 3. DISPATCH QUEUE ITEMS CONTAINER */}
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {paginatedDispatchItems.map((item) => {
          if (item.isBatch) {
            return (
              <BulkDispatchBatchCard
                key={item.batch_id}
                batch={item}
                canAct={canAct}
                user={user}
                busyIds={busyIds}
                optimisticStatus={optimisticStatus}
                onOpenOrder={onOpen}
                onOpenBatch={(batchId) => onOpen(batchId)}
                onAssignDelivery={(data) => setAssignModalData(data)}
                onDirectOutForDelivery={handleDirectOutForDelivery}
                onBatchDirectOutForDelivery={handleBatchDirectOutForDelivery}
              />
            );
          }

          // Standalone Single Order Card
          const info = getOrderDispatchInfo(item, optimisticStatus);
          const isUrgent = item.priority === "Urgent";
          const isHigh = item.priority === "High";
          const isBusy = busyIds.has(item.order_id);

          return (
            <div
              key={item.order_id}
              className="panel"
              style={{
                margin: 0,
                padding: "15px 18px",
                border: "1px solid #e2e8f0",
                borderRadius: 8,
                transition: "box-shadow 0.15s ease",
              }}
              data-testid={`single-dispatch-card-${item.order_id}`}
            >
              {/* Header: Order ID, Priority, Due Date & Stage Status Badge */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <b className="mono" style={{ color: "#2563eb", fontSize: 13 }}>#{item.order_id}</b>
                  <span className={`badge ${isUrgent ? "red" : isHigh ? "amber" : "blue"}`} style={{ fontSize: 10 }}>
                    {item.priority || "Normal"}
                  </span>
                  {item.required_delivery_date && (
                    <small className="muted" style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <Calendar size={11} /> Due {item.required_delivery_date}
                    </small>
                  )}
                </div>

                {/* Non-clickable status badge */}
                <span className={`badge ${info.statusBadge}`} style={{ fontSize: 11, fontWeight: 700 }}>
                  {info.statusLabel}
                </span>
              </div>

              {/* Main Content Grid: Client/Product, Quantity, Vendor, Production User, Warehouse, Delivery Staff, Challan, Direct Actions */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1.3fr 0.7fr 0.9fr 0.9fr 0.8fr 1.1fr 0.8fr auto",
                  gap: 12,
                  alignItems: "center",
                }}
              >
                {/* 1. Client & Product */}
                <div>
                  <h4 style={{ margin: 0, fontSize: 14, color: "var(--navy)" }}>{item.client_name}</h4>
                  <div style={{ fontSize: 13, color: "#1e293b", fontWeight: 600, marginTop: 2 }}>{item.product}</div>
                  <div
                    style={{
                      fontSize: 11.5,
                      color: item.description?.trim() ? "#475569" : "#94a3b8",
                      fontStyle: item.description?.trim() ? "normal" : "italic",
                      marginTop: 2,
                      lineHeight: 1.35,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={item.description?.trim() || "No description provided"}
                  >
                    {item.description?.trim() ? item.description.trim() : "No description provided"}
                  </div>
                  {item.packaging_instructions && (
                    <div
                      style={{ fontSize: 10.5, color: "#0284c7", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                      title={`Packaging: ${item.packaging_instructions}`}
                    >
                      📦 {item.packaging_instructions}
                    </div>
                  )}
                  {item.client_contact_person && (
                    <small className="muted" style={{ display: "block", marginTop: 2 }}>
                      Contact: {item.client_contact_person}
                      {canSeePhone && item.client_phone ? ` · 📞 ${item.client_phone}` : ""}
                    </small>
                  )}
                </div>

                {/* 2. Quantity & Value */}
                <div>
                  <small className="muted" style={{ display: "block", fontSize: 10, textTransform: "uppercase" }}>Quantity</small>
                  <b style={{ fontSize: 13 }}>{(item.quantity || 0).toLocaleString()} {item.unit || "units"}</b>
                  {item.total_value > 0 && <div style={{ fontSize: 11, color: "#64748b" }}>{compactMoney(item.total_value)}</div>}
                </div>

                {/* 3. Production Vendor */}
                <div>
                  <small className="muted" style={{ display: "block", fontSize: 10, textTransform: "uppercase" }}>Vendor</small>
                  {item.production_vendor_name ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12 }}>
                      <Factory size={13} color="#4f46e5" />
                      <b>{item.production_vendor_name}</b>
                    </div>
                  ) : (
                    <span style={{ color: "#94a3b8", fontSize: 12 }}>—</span>
                  )}
                </div>

                {/* 4. Assigned Production User */}
                <div>
                  <small className="muted" style={{ display: "block", fontSize: 10, textTransform: "uppercase" }}>Production User</small>
                  {item.assigned_producer || item.assigned_production_user ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12 }}>
                      <User size={13} color="#059669" />
                      <b style={{ color: "#059669" }}>{item.assigned_producer || item.assigned_production_user}</b>
                    </div>
                  ) : (
                    <span style={{ color: "#d97706", fontSize: 11 }}>Unassigned</span>
                  )}
                </div>

                {/* 5. Warehouse */}
                <div>
                  <small className="muted" style={{ display: "block", fontSize: 10, textTransform: "uppercase" }}>Warehouse</small>
                  {item.warehouse_name ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12 }}>
                      <Warehouse size={13} color="#0284c7" />
                      <b>{item.warehouse_name}</b>
                    </div>
                  ) : (
                    <span style={{ color: "#94a3b8", fontSize: 12 }}>—</span>
                  )}
                </div>

                {/* 4. Delivery Staff (with Change button) */}
                <div>
                  <small className="muted" style={{ display: "block", fontSize: 10, textTransform: "uppercase" }}>Delivery Staff</small>
                  {info.hasStaff ? (
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap" }}>
                        <b style={{ fontSize: 12, color: "#1e293b" }}>{info.order.assigned_delivery_person}</b>
                        {canAct && !info.isDel && (
                          <button
                            type="button"
                            className="btn-link-subtle"
                            onClick={(e) => {
                              e.stopPropagation();
                              setAssignModalData({ mode: "single", order: info.order, targetOrders: [info.order] });
                            }}
                            title="Change Delivery Staff"
                            disabled={isBusy}
                          >
                            <Edit2 size={10} /> Change
                          </button>
                        )}
                      </div>
                      {info.order.delivery_person_phone && (
                        <div style={{ fontSize: 11, color: "#64748b" }}>📞 {info.order.delivery_person_phone}</div>
                      )}
                    </div>
                  ) : (
                    <span className="badge amber" style={{ fontSize: 10.5 }}>Unassigned</span>
                  )}
                </div>

                {/* 5. Delivery Challan (read-only reference if present) */}
                <div>
                  <small className="muted" style={{ display: "block", fontSize: 10, textTransform: "uppercase" }}>Delivery Challan</small>
                  {info.hasChallan ? (
                    <span className="mono" style={{ fontSize: 12, fontWeight: 700, color: "#0f172a" }}>
                      #{info.order.challan_number}
                    </span>
                  ) : (
                    <span style={{ fontSize: 11.5, color: "#94a3b8" }}>—</span>
                  )}
                </div>

                {/* 6. Direct Actions: Assign Delivery Person / Out for Delivery + Manage */}
                <div style={{ textAlign: "right", display: "flex", gap: 6, alignItems: "center", justifyContent: "flex-end", flexWrap: "wrap" }}>
                  {/* Step 1: Assign Delivery Person (when unassigned) */}
                  {canAct && !info.isDel && !info.isOut && !info.hasStaff && (
                    <button
                      type="button"
                      className="prod-action-btn prod-action-btn-assign btn-md"
                      onClick={(e) => {
                        e.stopPropagation();
                        setAssignModalData({ mode: "single", order: info.order, targetOrders: [info.order] });
                      }}
                      disabled={isBusy}
                      title="Assign a delivery person"
                      data-testid={`single-assign-btn-${item.order_id}`}
                    >
                      <UserCheck size={12} />
                      <span>{isBusy ? "..." : "Assign Delivery Person"}</span>
                    </button>
                  )}

                  {/* Step 2: Out for Delivery (when Delivery Person is assigned) */}
                  {canAct && !info.isDel && !info.isOut && info.hasStaff && (
                    <button
                      type="button"
                      className="prod-action-btn prod-action-btn-out btn-md"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDirectOutForDelivery(info.order);
                      }}
                      disabled={isBusy}
                      title="Send this order Out for Delivery"
                      data-testid={`single-out-btn-${item.order_id}`}
                    >
                      <Truck size={12} />
                      <span>{isBusy ? "Sending..." : "Out for Delivery"}</span>
                    </button>
                  )}

                  {/* Manage Button */}
                  <button
                    type="button"
                    className="prod-action-btn prod-action-btn-manage btn-md"
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpen(item.order_id);
                    }}
                    data-testid={`manage-dispatch-btn-${item.order_id}`}
                  >
                    Manage
                  </button>
                </div>
              </div>

              {item.dispatch_remarks && (
                <div
                  style={{
                    background: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    borderRadius: 6,
                    padding: "6px 10px",
                    marginTop: 8,
                    fontSize: 12,
                    color: "#475569",
                  }}
                >
                  <b>Remarks:</b> {item.dispatch_remarks}
                </div>
              )}
            </div>
          );
        })}

        {filteredDispatchItems.length === 0 && (
          <div className="panel" style={{ padding: "48px 24px", textAlign: "center", border: "1px dashed #cbd5e1" }} data-testid="dispatch-empty-state">
            <div style={{ color: "var(--navy)", fontWeight: 600, fontSize: 14, marginBottom: 4 }}>No orders in dispatch</div>
            <span className="muted" style={{ fontSize: 13 }}>
              {search || statusFilter || priorityFilter || vendorFilter || deliveryPersonFilter
                ? "No dispatch requests match your active search or filter criteria."
                : "There are currently no orders ready for dispatch."}
            </span>
          </div>
        )}
      </div>

      {/* Pagination */}
      {filteredDispatchItems.length > 0 && (
        <div style={{ padding: "14px 0 0", borderTop: "1px solid #f1f5f9", marginTop: 14 }}>
          <Pagination
            page={page}
            pageSize={pageSize}
            totalItems={filteredDispatchItems.length}
            onPageChange={setPage}
            onPageSizeChange={(newSize) => {
              setPageSize(newSize);
              setPage(1);
            }}
            itemName="dispatch requests"
          />
        </div>
      )}

      {/* ============================================================== */}
      {/* ACTION MODALS                                                  */}
      {/* ============================================================== */}

      {/* 1. Assign Delivery Staff Modal */}
      {assignModalData && (
        <AssignDeliveryModal
          data={assignModalData}
          deliveryAgents={deliveryAgents}
          isOpen={Boolean(assignModalData)}
          onClose={() => setAssignModalData(null)}
          onConfirm={handleConfirmAssign}
        />
      )}

      {/* 2. Attach / Generate Challan Modal */}
      {challanModalData && (
        <AttachChallanModal
          data={challanModalData}
          isOpen={Boolean(challanModalData)}
          onClose={() => setChallanModalData(null)}
          onConfirm={handleConfirmChallan}
        />
      )}

      {/* 3. Send Out for Delivery Modal */}
      {outModalData && (
        <OutForDeliveryModal
          data={outModalData}
          isOpen={Boolean(outModalData)}
          onClose={() => setOutModalData(null)}
          onConfirm={handleConfirmOutForDelivery}
        />
      )}

    </div>
  );
}

// -------------------------------------------------------------
// Metric Box Component
// -------------------------------------------------------------
function MetricBox({ label, value, tone = "blue", onClick, active }) {
  return (
    <div
      className={`metric ${tone}`}
      onClick={onClick}
      style={{
        cursor: onClick ? "pointer" : "default",
        userSelect: "none",
        ...(active ? { outline: "2px solid var(--navy)", outlineOffset: "-1px" } : {}),
      }}
      title={onClick ? `Filter by ${label}` : undefined}
      data-testid={`dispatch-metric-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    >
      <div className="metric-top">
        <span>{label}</span>
        {active && <span style={{ fontSize: 10, fontWeight: 700, color: "var(--navy)" }}>FILTERED</span>}
      </div>
      <strong>{value}</strong>
    </div>
  );
}

// -------------------------------------------------------------
// Bulk Dispatch Batch Card
// -------------------------------------------------------------
function BulkDispatchBatchCard({
  batch,
  canAct,
  user,
  busyIds,
  optimisticStatus,
  onOpenOrder,
  onOpenBatch,
  onAssignDelivery,
  onDirectOutForDelivery,
  onBatchDirectOutForDelivery,
}) {
  const isUrgent = batch.priority === "Urgent";
  const isHigh = batch.priority === "High";
  const canSeePhone = canViewClientPhone(user);

  // Row selection state for targeted bulk actions inside this batch
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);

  const toggleSelectOrder = (orderId) => {
    setSelectedOrderIds((prev) =>
      prev.includes(orderId) ? prev.filter((id) => id !== orderId) : [...prev, orderId]
    );
  };

  const isAllSelected =
    batch.orders.length > 0 && selectedOrderIds.length === batch.orders.length;

  const toggleSelectAll = () => {
    if (isAllSelected) setSelectedOrderIds([]);
    else setSelectedOrderIds(batch.orders.map((o) => o.order_id));
  };

  // Determine which orders to target for bulk actions:
  // If specific checkboxes are checked, use only selected products.
  // Otherwise, use all products in the batch.
  const activeOrdersPool = useMemo(() => {
    if (selectedOrderIds.length > 0) {
      return batch.orders.filter((o) => selectedOrderIds.includes(o.order_id));
    }
    return batch.orders;
  }, [batch.orders, selectedOrderIds]);

  // Compute eligible products for each bulk action
  const eligibleAssign = useMemo(() => {
    return activeOrdersPool.filter((o) => {
      const info = getOrderDispatchInfo(o, optimisticStatus);
      return !info.isDel && !info.isOut && (!info.hasStaff || selectedOrderIds.length > 0);
    });
  }, [activeOrdersPool, optimisticStatus, selectedOrderIds]);

  const eligibleOut = useMemo(() => {
    return activeOrdersPool.filter((o) => {
      const info = getOrderDispatchInfo(o, optimisticStatus);
      return !info.isDel && !info.isOut && info.hasStaff;
    });
  }, [activeOrdersPool, optimisticStatus]);

  const statusTheme = (st) => {
    switch (st) {
      case "Delivered":
        return { color: "#059669", bg: "#ecfdf5", border: "#a7f3d0", label: "Delivered" };
      case "Out for Delivery":
        return { color: "#7c3aed", bg: "#f5f3ff", border: "#ddd6fe", label: "Out for Delivery" };
      case "Ready for Delivery":
      case "Delivery Assigned":
        return { color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe", label: "Delivery Assigned" };
      case "Partially Assigned":
        return { color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe", label: "Partially Assigned" };
      default:
        return { color: "#d97706", bg: "#fffbeb", border: "#fde68a", label: "Ready for Dispatch" };
    }
  };

  const currentTheme = statusTheme(batch.dispatch_status);

  return (
    <div
      className="bulk-batch-card panel"
      style={{
        margin: 0,
        background: "#ffffff",
        border: "1.5px solid #cbd5e1",
        borderRadius: 9,
        padding: "16px 18px",
        boxShadow: "0 2px 6px rgba(15, 23, 42, 0.05)",
      }}
      data-testid={`bulk-dispatch-batch-card-${batch.batch_id}`}
    >
      {/* Top Row: Batch ID, Tag, Priority & Status */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, flexWrap: "wrap", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            className="mono"
            style={{
              background: "#eff6ff",
              color: "#2563eb",
              border: "1px solid #bfdbfe",
              padding: "2px 8px",
              borderRadius: 4,
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            #{batch.batch_id}
          </span>
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "#475569",
              background: "#f1f5f9",
              padding: "2px 7px",
              borderRadius: 4,
              border: "1px solid #e2e8f0",
            }}
          >
            Bulk Dispatch Request · {batch.orders.length} Products
          </span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span className={`badge ${isUrgent ? "red" : isHigh ? "amber" : "blue"}`} style={{ fontSize: 10 }}>
            {batch.priority}
          </span>
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              padding: "2px 8px",
              borderRadius: 4,
              color: currentTheme.color,
              background: currentTheme.bg,
              border: `1px solid ${currentTheme.border}`,
            }}
          >
            {currentTheme.label}
          </span>
        </div>
      </div>

      {/* Client Name & Summary Row */}
      <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr 1fr", gap: 12, alignItems: "center", marginBottom: 12 }}>
        <div>
          <h3 style={{ margin: "2px 0 2px", fontSize: 16, fontWeight: 700, color: "var(--navy)" }}>
            {batch.client_name}
          </h3>
          {batch.client_contact_person && (
            <div style={{ fontSize: 12, color: "#64748b" }}>
              Contact: {batch.client_contact_person}
              {canSeePhone && batch.client_phone ? ` · 📞 ${batch.client_phone}` : ""}
            </div>
          )}
        </div>

        <div>
          <small className="muted" style={{ display: "block", fontSize: 10.5, textTransform: "uppercase" }}>Total Quantity</small>
          <b>{(batch.total_quantity || 0).toLocaleString()} units</b>
          {batch.total_value > 0 && <small className="muted" style={{ display: "block", fontSize: 11 }}>{compactMoney(batch.total_value)}</small>}
        </div>

        <div>
          <small className="muted" style={{ display: "block", fontSize: 10.5, textTransform: "uppercase" }}>Production Vendor</small>
          {batch.production_vendor_name ? (
            <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12 }}>
              <Factory size={13} color="#4f46e5" />
              <b>{batch.production_vendor_name}</b>
            </div>
          ) : (
            <span style={{ color: "#94a3b8", fontSize: 12 }}>—</span>
          )}
        </div>

        <div>
          <small className="muted" style={{ display: "block", fontSize: 10.5, textTransform: "uppercase" }}>Production User</small>
          {batch.assigned_producer ? (
            <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12 }}>
              <User size={13} color="#059669" />
              <b style={{ color: "#059669" }}>{batch.assigned_producer}</b>
            </div>
          ) : (
            <span style={{ color: "#d97706", fontSize: 11 }}>Unassigned</span>
          )}
        </div>

        <div>
          <small className="muted" style={{ display: "block", fontSize: 10.5, textTransform: "uppercase" }}>Warehouse</small>
          {batch.warehouse_name ? (
            <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12 }}>
              <Warehouse size={13} color="#0284c7" />
              <b>{batch.warehouse_name}</b>
            </div>
          ) : (
            <span style={{ color: "#94a3b8", fontSize: 12 }}>—</span>
          )}
        </div>

        <div>
          <small className="muted" style={{ display: "block", fontSize: 10.5, textTransform: "uppercase" }}>Delivery Staff</small>
          {batch.assigned_delivery_person ? (
            <div style={{ fontSize: 12, color: "#334155" }}>
              <b>{batch.assigned_delivery_person}</b>
              {batch.delivery_person_phone && (
                <div style={{ fontSize: 11, color: "#64748b" }}>📞 {batch.delivery_person_phone}</div>
              )}
            </div>
          ) : (
            <span className="badge amber" style={{ fontSize: 10.5 }}>Unassigned</span>
          )}
        </div>
      </div>

      {/* Itemized Table of Products in Batch with Direct Actions */}
      <div
        style={{
          background: "#f8fafc",
          border: "1px solid #e2e8f0",
          borderRadius: 6,
          padding: "8px 12px",
          marginBottom: 12,
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "24px 85px 1.3fr 65px 95px 105px 90px 110px 85px 100px auto",
            gap: 8,
            fontSize: 10.5,
            fontWeight: 700,
            color: "#64748b",
            textTransform: "uppercase",
            borderBottom: "1px solid #e2e8f0",
            paddingBottom: 4,
            marginBottom: 6,
            alignItems: "center",
          }}
        >
          <span>
            <input
              type="checkbox"
              checked={isAllSelected}
              onChange={toggleSelectAll}
              title="Select all products in this request"
              style={{ cursor: "pointer" }}
            />
          </span>
          <span>Order ID</span>
          <span>Product</span>
          <span>Qty</span>
          <span>Vendor</span>
          <span>Production User</span>
          <span>Warehouse</span>
          <span>Delivery Staff</span>
          <span>Challan</span>
          <span>Status</span>
          <span style={{ textAlign: "right" }}>Action</span>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {batch.orders.map((rawOrder) => {
            const info = getOrderDispatchInfo(rawOrder, optimisticStatus);
            const o = info.order;
            const isBusy = busyIds?.has(o.order_id);
            const isSelected = selectedOrderIds.includes(o.order_id);

            return (
              <div
                key={o.order_id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "24px 85px 1.3fr 65px 95px 105px 90px 110px 85px 100px auto",
                  gap: 8,
                  fontSize: 12,
                  alignItems: "center",
                  padding: "4px 0",
                  borderBottom: "1px solid #f1f5f9",
                  background: isSelected ? "#f0f9ff" : "transparent",
                  borderRadius: 4,
                }}
              >
                {/* 1. Selection checkbox */}
                <span>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleSelectOrder(o.order_id)}
                    style={{ cursor: "pointer" }}
                    data-testid={`batch-item-checkbox-${o.order_id}`}
                  />
                </span>

                {/* 2. Order ID */}
                <span className="mono" style={{ fontWeight: 600, color: "#2563eb", fontSize: 11.5 }}>
                  #{o.order_id}
                </span>

                {/* 3. Product Title & Description */}
                <div style={{ minWidth: 0 }}>
                  <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={o.product}>
                    <b style={{ color: "#1e293b" }}>{o.product}</b>
                    {o.product_code && <span style={{ color: "#64748b", fontSize: 11, marginLeft: 4 }}>({o.product_code})</span>}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: o.description?.trim() ? "#475569" : "#94a3b8",
                      fontStyle: o.description?.trim() ? "normal" : "italic",
                      marginTop: 2,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={o.description?.trim() || "No description provided"}
                  >
                    {o.description?.trim() ? o.description.trim() : "No description provided"}
                  </div>
                  {o.packaging_instructions && (
                    <div
                      style={{
                        fontSize: 10,
                        color: "#0284c7",
                        marginTop: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={`Packaging: ${o.packaging_instructions}`}
                    >
                      📦 {o.packaging_instructions}
                    </div>
                  )}
                </div>

                {/* 4. Quantity */}
                <span>{(o.quantity || 0).toLocaleString()} {o.unit || "pcs"}</span>

                {/* 5. Production Vendor */}
                <div style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 11 }}>
                  {o.production_vendor_name ? (
                    <span style={{ color: "var(--navy)", fontWeight: 600 }} title={o.production_vendor_name}>{o.production_vendor_name}</span>
                  ) : (
                    <span style={{ color: "#94a3b8" }}>—</span>
                  )}
                </div>

                {/* 6. Assigned Production User */}
                <div style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 11 }}>
                  {o.assigned_producer || o.assigned_production_user ? (
                    <span style={{ color: "#059669", fontWeight: 600 }} title={o.assigned_producer || o.assigned_production_user}>
                      {o.assigned_producer || o.assigned_production_user}
                    </span>
                  ) : (
                    <span style={{ color: "#d97706" }}>Unassigned</span>
                  )}
                </div>

                {/* 7. Warehouse */}
                <div style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 11 }}>
                  {o.warehouse_name ? (
                    <span style={{ color: "#334155" }} title={o.warehouse_name}>{o.warehouse_name}</span>
                  ) : (
                    <span style={{ color: "#94a3b8" }}>—</span>
                  )}
                </div>

                {/* 6. Delivery Staff */}
                <div style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 11.5 }}>
                  {info.hasStaff ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <b style={{ color: "#334155" }}>{o.assigned_delivery_person}</b>
                      {canAct && !info.isDel && (
                        <button
                          type="button"
                          className="btn-link-subtle"
                          onClick={() => onAssignDelivery({ mode: "single", order: o, batch, targetOrders: [o] })}
                          title="Change Delivery Staff"
                          disabled={isBusy}
                        >
                          <Edit2 size={10} />
                        </button>
                      )}
                    </div>
                  ) : (
                    <span className="badge amber" style={{ fontSize: 9.5, padding: "1px 5px" }}>Unassigned</span>
                  )}
                </div>

                {/* 7. Challan (read-only reference) */}
                <div style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 11 }}>
                  {info.hasChallan ? (
                    <span className="mono" style={{ fontWeight: 600, color: "#0f172a" }}>
                      #{o.challan_number}
                    </span>
                  ) : (
                    <span style={{ color: "#94a3b8" }}>—</span>
                  )}
                </div>

                {/* 8. Non-clickable status badge */}
                <div>
                  <span className={`badge ${info.statusBadge}`} style={{ fontSize: 10, padding: "2px 6px" }}>
                    {info.statusLabel}
                  </span>
                </div>

                {/* 9. Direct Actions: Next Step Button + Manage Button */}
                <div style={{ textAlign: "right", display: "flex", gap: 4, justifyContent: "flex-end", alignItems: "center" }}>
                  {/* Step 1: Assign Delivery Person */}
                  {canAct && !info.isDel && !info.isOut && !info.hasStaff && (
                    <button
                      type="button"
                      className="prod-action-btn prod-action-btn-assign btn-sm"
                      onClick={() => onAssignDelivery({ mode: "single", order: o, batch, targetOrders: [o] })}
                      disabled={isBusy}
                      title="Assign Delivery Staff for this product"
                      data-testid={`batch-item-assign-btn-${o.order_id}`}
                    >
                      <UserCheck size={10} /> Assign
                    </button>
                  )}

                  {/* Step 2: Out for Delivery (when Delivery Person is assigned) */}
                  {canAct && !info.isDel && !info.isOut && info.hasStaff && (
                    <button
                      type="button"
                      className="prod-action-btn prod-action-btn-out btn-sm"
                      onClick={() => onDirectOutForDelivery(o)}
                      disabled={isBusy}
                      title="Send this product Out for Delivery"
                      data-testid={`batch-item-out-btn-${o.order_id}`}
                    >
                      <Truck size={10} /> {isBusy ? "..." : "Out for Delivery"}
                    </button>
                  )}


                  {/* Manage Button */}
                  <button
                    type="button"
                    className="prod-action-btn prod-action-btn-manage btn-sm"
                    onClick={() => onOpenOrder(o.order_id)}
                    style={{ padding: "2px 7px", fontSize: 11 }}
                    data-testid={`batch-item-manage-btn-${o.order_id}`}
                  >
                    Manage
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer: Summary breakdown & Bulk Action buttons */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
        {/* Breakdown & selection badge */}
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, color: "#64748b", flexWrap: "wrap" }}>
          <span style={{ fontWeight: 700 }}>Summary:</span>
          {batch.counts.ready > 0 && <span>{batch.counts.ready} Ready for Dispatch</span>}
          {batch.counts.outForDelivery > 0 && <span>· {batch.counts.outForDelivery} Out for Delivery</span>}
          {batch.counts.delivered > 0 && <span>· {batch.counts.delivered} Delivered</span>}

          {selectedOrderIds.length > 0 && (
            <span
              style={{
                marginLeft: 4,
                background: "#eff6ff",
                color: "#1d4ed8",
                padding: "2px 8px",
                borderRadius: 4,
                fontWeight: 600,
                border: "1px solid #bfdbfe",
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
              }}
            >
              {selectedOrderIds.length} of {batch.orders.length} selected
              <button
                type="button"
                onClick={() => setSelectedOrderIds([])}
                style={{ background: "none", border: "none", cursor: "pointer", color: "#1d4ed8", padding: 0 }}
                title="Clear row selections"
              >
                <X size={12} />
              </button>
            </span>
          )}
        </div>

        {/* Bulk Action Buttons (Valid only for eligible products) */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          {/* Bulk Assign */}
          {canAct && eligibleAssign.length > 0 && (
            <button
              type="button"
              className="prod-action-btn prod-action-btn-assign btn-md"
              onClick={() => onAssignDelivery({ mode: "batch", batch, targetOrders: eligibleAssign })}
              data-testid={`batch-card-assign-btn-${batch.batch_id}`}
              title={`Assign delivery staff to ${eligibleAssign.length} eligible product(s)`}
            >
              <UserCheck size={12} /> Assign Delivery Staff ({eligibleAssign.length})
            </button>
          )}

          {/* Bulk Send Out for Delivery */}
          {canAct && eligibleOut.length > 0 && (
            <button
              type="button"
              className="prod-action-btn prod-action-btn-out btn-md"
              onClick={() => onBatchDirectOutForDelivery(batch, eligibleOut)}
              data-testid={`batch-card-out-btn-${batch.batch_id}`}
              title={`Send ${eligibleOut.length} eligible product(s) Out for Delivery`}
            >
              <Truck size={12} /> Out for Delivery ({eligibleOut.length})
            </button>
          )}



          <button
            type="button"
            className="primary-btn compact"
            style={{ background: "#4f46e5", borderColor: "#4f46e5", display: "inline-flex", alignItems: "center", gap: 4 }}
            onClick={() => onOpenBatch(batch.batch_id)}
            data-testid={`manage-batch-btn-${batch.batch_id}`}
          >
            Manage Dispatch Request <ArrowRight size={13} />
          </button>
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------------------
// 1. Assign Delivery Staff Modal (Single or Batch)
// -------------------------------------------------------------
function AssignDeliveryModal({ data, deliveryAgents, isOpen, onClose, onConfirm }) {
  const [selectedAgentId, setSelectedAgentId] = useState("");
  const [customName, setCustomName] = useState("");
  const [customPhone, setCustomPhone] = useState("");
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (data && data.order?.assigned_delivery_person) {
      const match = deliveryAgents.find(
        (a) => a.name?.toLowerCase() === data.order.assigned_delivery_person?.toLowerCase()
      );
      if (match) setSelectedAgentId(match.user_id || match.id || match.name);
    }
  }, [data, deliveryAgents]);

  if (!isOpen || !data) return null;

  const { mode, order, batch, targetOrders } = data;
  const isBatch = mode === "batch" || targetOrders.length > 1;

  const handleSubmit = async (e) => {
    e.preventDefault();
    let name = "";
    let phone = "";
    let id = "";

    if (selectedAgentId === "custom") {
      name = customName.trim();
      phone = customPhone.trim();
      if (!name) {
        toast.error("Please enter the delivery person's name");
        return;
      }
    } else {
      const agent = deliveryAgents.find(
        (a) => (a.user_id && a.user_id === selectedAgentId) || a.name === selectedAgentId
      );
      if (!agent) {
        toast.error("Please select a delivery person");
        return;
      }
      name = agent.name;
      phone = agent.phone || "";
      id = agent.user_id || agent.id || "";
    }

    setBusy(true);
    try {
      await onConfirm({
        mode,
        order,
        batch,
        targetOrders,
        agentName: name,
        agentPhone: phone,
        agentId: id,
        remarks: remarks.trim(),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 23, 42, 0.6)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
      onClick={onClose}
    >
      <div
        className="panel"
        style={{
          width: "100%",
          maxWidth: 500,
          background: "#fff",
          borderRadius: 10,
          boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
          padding: 24,
        }}
        onClick={(e) => e.stopPropagation()}
        data-testid="assign-delivery-staff-modal"
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <h3 style={{ margin: 0, fontSize: 17, color: "var(--navy)", display: "flex", alignItems: "center", gap: 6 }}>
            <UserCheck size={18} color="#2563eb" /> Assign Delivery Staff
          </h3>
          <button type="button" className="icon-btn" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        {/* Affected Orders Context */}
        <div
          style={{
            background: "#eff6ff",
            border: "1px solid #bfdbfe",
            borderRadius: 6,
            padding: "8px 12px",
            marginBottom: 16,
            fontSize: 12.5,
            color: "#1e40af",
          }}
        >
          {isBatch ? (
            <div>
              <b>Bulk Action:</b> Assigning delivery staff to <b>{targetOrders.length} eligible products</b> in #{batch?.batch_id} ({batch?.client_name}).
              <div style={{ fontSize: 11, color: "#3b82f6", marginTop: 4, fontFamily: "monospace" }}>
                Orders: {targetOrders.map((o) => `#${o.order_id}`).join(", ")}
              </div>
            </div>
          ) : (
            <div>
              <b>Order #{order?.order_id}:</b> {order?.product} · Client: <b>{order?.client_name}</b>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit}>
          <div className="dispatch-field" style={{ marginBottom: 14 }}>
            <label className="dispatch-label" style={{ display: "block", fontWeight: 600, fontSize: 12.5, marginBottom: 6 }}>
              Select Delivery Person <span style={{ color: "#dc2626" }}>*</span>
            </label>
            <select
              className="dispatch-control"
              value={selectedAgentId}
              onChange={(e) => setSelectedAgentId(e.target.value)}
              required
              style={{ width: "100%", padding: "9px 12px", borderRadius: 6, border: "1px solid var(--line)" }}
              data-testid="assign-delivery-staff-select"
            >
              <option value="">-- Choose Delivery Person --</option>
              {deliveryAgents.map((agent) => (
                <option key={agent.user_id || agent.name} value={agent.user_id || agent.name}>
                  {agent.name} {agent.phone ? `(${agent.phone})` : ""}
                </option>
              ))}
              <option value="custom">+ Enter New Delivery Person</option>
            </select>
          </div>

          {selectedAgentId === "custom" && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
              <div>
                <label className="dispatch-label" style={{ display: "block", fontWeight: 600, fontSize: 12, marginBottom: 4 }}>
                  Name *
                </label>
                <input
                  className="dispatch-control"
                  type="text"
                  placeholder="e.g. Suresh Kumar"
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                  required
                  style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid var(--line)", fontSize: 13 }}
                />
              </div>
              <div>
                <label className="dispatch-label" style={{ display: "block", fontWeight: 600, fontSize: 12, marginBottom: 4 }}>
                  Phone
                </label>
                <input
                  className="dispatch-control"
                  type="text"
                  placeholder="e.g. +91 98201 55667"
                  value={customPhone}
                  onChange={(e) => setCustomPhone(e.target.value)}
                  style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid var(--line)", fontSize: 13 }}
                />
              </div>
            </div>
          )}

          <div className="dispatch-field" style={{ marginBottom: 18 }}>
            <label className="dispatch-label" style={{ display: "block", fontWeight: 600, fontSize: 12.5, marginBottom: 6 }}>
              Remarks (Optional)
            </label>
            <input
              className="dispatch-control"
              type="text"
              placeholder="e.g. Scheduled for morning delivery route"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid var(--line)", fontSize: 13 }}
            />
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button type="button" className="outline-btn" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button
              type="submit"
              className="primary-btn"
              disabled={busy || !selectedAgentId}
              style={{ background: "#2563eb", borderColor: "#2563eb" }}
              data-testid="confirm-assign-delivery-btn"
            >
              {busy ? "Assigning…" : "Confirm Assignment"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// -------------------------------------------------------------
// 2. Attach / Generate Challan Modal (Single or Batch)
// -------------------------------------------------------------
function AttachChallanModal({ data, isOpen, onClose, onConfirm }) {
  const [actionType, setActionType] = useState("generate"); // 'generate' | 'manual'
  const [customChallanNumber, setCustomChallanNumber] = useState("");
  const [busy, setBusy] = useState(false);

  if (!isOpen || !data) return null;

  const { mode, order, batch, targetOrders } = data;
  const isBatch = mode === "batch" || targetOrders.length > 1;
  const currentChallan = targetOrders.find((o) => o.challan_number)?.challan_number;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (actionType === "manual" && !customChallanNumber.trim()) {
      toast.error("Please enter a Challan Number");
      return;
    }

    setBusy(true);
    try {
      await onConfirm({
        mode,
        order,
        batch,
        targetOrders,
        actionType,
        customChallanNumber: customChallanNumber.trim(),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 23, 42, 0.6)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
      onClick={onClose}
    >
      <div
        className="panel"
        style={{
          width: "100%",
          maxWidth: 480,
          background: "#fff",
          borderRadius: 10,
          boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
          padding: 24,
        }}
        onClick={(e) => e.stopPropagation()}
        data-testid="attach-challan-modal"
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <h3 style={{ margin: 0, fontSize: 17, color: "var(--navy)", display: "flex", alignItems: "center", gap: 6 }}>
            <FileText size={18} color="#ea580c" /> Attach Delivery Challan
          </h3>
          <button type="button" className="icon-btn" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        {/* Target Context */}
        <div
          style={{
            background: "#fff7ed",
            border: "1px solid #fed7aa",
            borderRadius: 6,
            padding: "8px 12px",
            marginBottom: 16,
            fontSize: 12.5,
            color: "#9a3412",
          }}
        >
          {isBatch ? (
            <div>
              Attaching Delivery Challan to <b>{targetOrders.length} products</b> in #{batch?.batch_id}.
              <div style={{ fontSize: 11, color: "#c2410c", marginTop: 4, fontFamily: "monospace" }}>
                {targetOrders.map((o) => `#${o.order_id}`).join(", ")}
              </div>
            </div>
          ) : (
            <div>
              Order <b>#{order?.order_id}</b> · {order?.product}
            </div>
          )}
          {currentChallan && (
            <div style={{ marginTop: 4, fontSize: 11.5, fontWeight: 600 }}>
              Current Challan: #{currentChallan} (will be updated)
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit}>
          {/* Method Selection */}
          <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
            <label
              style={{
                flex: 1,
                border: actionType === "generate" ? "2px solid #ea580c" : "1px solid #e2e8f0",
                background: actionType === "generate" ? "#fff7ed" : "#fff",
                borderRadius: 6,
                padding: "10px 12px",
                cursor: "pointer",
                display: "block",
              }}
            >
              <input
                type="radio"
                name="challanMethod"
                checked={actionType === "generate"}
                onChange={() => setActionType("generate")}
                style={{ marginRight: 6 }}
              />
              <b style={{ fontSize: 12.5, color: "#1e293b" }}>Auto-Generate System Challan</b>
              <small className="muted" style={{ display: "block", fontSize: 11, marginTop: 4 }}>
                Generates official Challan PDF & unique number
              </small>
            </label>

            <label
              style={{
                flex: 1,
                border: actionType === "manual" ? "2px solid #ea580c" : "1px solid #e2e8f0",
                background: actionType === "manual" ? "#fff7ed" : "#fff",
                borderRadius: 6,
                padding: "10px 12px",
                cursor: "pointer",
                display: "block",
              }}
            >
              <input
                type="radio"
                name="challanMethod"
                checked={actionType === "manual"}
                onChange={() => setActionType("manual")}
                style={{ marginRight: 6 }}
              />
              <b style={{ fontSize: 12.5, color: "#1e293b" }}>Enter Existing Challan #</b>
              <small className="muted" style={{ display: "block", fontSize: 11, marginTop: 4 }}>
                Use an external paper/vendor Challan number
              </small>
            </label>
          </div>

          {actionType === "manual" && (
            <div className="dispatch-field" style={{ marginBottom: 16 }}>
              <label className="dispatch-label" style={{ display: "block", fontWeight: 600, fontSize: 12.5, marginBottom: 6 }}>
                Challan Number <span style={{ color: "#dc2626" }}>*</span>
              </label>
              <input
                type="text"
                className="dispatch-control"
                placeholder="e.g. CH-2026-9081"
                value={customChallanNumber}
                onChange={(e) => setCustomChallanNumber(e.target.value)}
                required
                style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid var(--line)", fontSize: 13 }}
                data-testid="custom-challan-number-input"
              />
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button type="button" className="outline-btn" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button
              type="submit"
              className="primary-btn"
              disabled={busy}
              style={{ background: "#ea580c", borderColor: "#ea580c" }}
              data-testid="confirm-attach-challan-btn"
            >
              {busy ? "Processing…" : actionType === "generate" ? "Generate & Attach Challan" : "Attach Challan"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// -------------------------------------------------------------
// 3. Send Out for Delivery Modal (Single or Batch)
// -------------------------------------------------------------
function OutForDeliveryModal({ data, isOpen, onClose, onConfirm }) {
  const [transporter, setTransporter] = useState("");
  const [trackingNumber, setTrackingNumber] = useState("");
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (data) {
      const defaultPerson =
        data.order?.assigned_delivery_person ||
        data.batch?.assigned_delivery_person ||
        data.targetOrders?.[0]?.assigned_delivery_person ||
        "In-House Delivery";
      setTransporter(defaultPerson);
      setTrackingNumber(data.order?.tracking_number || data.batch?.tracking_number || "");
    }
  }, [data]);

  if (!isOpen || !data) return null;

  const { mode, order, batch, targetOrders } = data;
  const isBatch = mode === "batch" || targetOrders.length > 1;

  // Strict workflow validation: Check that all target orders have delivery staff
  const unassigned = targetOrders.filter((o) => !o.assigned_delivery_person);
  const hasValidationError = unassigned.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (hasValidationError) {
      toast.error("All target orders must have an assigned Delivery Person before dispatch.");
      return;
    }

    setBusy(true);
    try {
      await onConfirm({
        mode,
        order,
        batch,
        targetOrders,
        transporter: transporter.trim(),
        trackingNumber: trackingNumber.trim(),
        remarks: remarks.trim(),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 23, 42, 0.6)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
      onClick={onClose}
    >
      <div
        className="panel"
        style={{
          width: "100%",
          maxWidth: 500,
          background: "#fff",
          borderRadius: 10,
          boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
          padding: 24,
        }}
        onClick={(e) => e.stopPropagation()}
        data-testid="send-out-for-delivery-modal"
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <h3 style={{ margin: 0, fontSize: 17, color: "var(--navy)", display: "flex", alignItems: "center", gap: 6 }}>
            <Truck size={18} color="#7c3aed" /> Send Out for Delivery
          </h3>
          <button type="button" className="icon-btn" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        {/* Validation Error Banner */}
        {hasValidationError && (
          <div
            style={{
              background: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: 6,
              padding: "10px 12px",
              marginBottom: 16,
              fontSize: 12.5,
              color: "#991b1b",
              display: "flex",
              alignItems: "flex-start",
              gap: 8,
            }}
          >
            <AlertCircle size={16} style={{ marginTop: 2, flexShrink: 0 }} />
            <div>
              <b>Cannot proceed:</b>
              {unassigned.length > 0 && (
                <div>· {unassigned.length} order(s) missing Delivery Person assignment.</div>
              )}
            </div>
          </div>
        )}

        {/* Context info */}
        <div
          style={{
            background: "#f5f3ff",
            border: "1px solid #ddd6fe",
            borderRadius: 6,
            padding: "8px 12px",
            marginBottom: 16,
            fontSize: 12.5,
            color: "#6b21a8",
          }}
        >
          {isBatch ? (
            <div>
              Sending <b>{targetOrders.length} eligible products</b> in #{batch?.batch_id} Out for Delivery.
              <div style={{ fontSize: 11, color: "#7c3aed", marginTop: 4, fontFamily: "monospace" }}>
                {targetOrders.map((o) => `#${o.order_id}`).join(", ")}
              </div>
            </div>
          ) : (
            <div>
              Order <b>#{order?.order_id}</b> · {order?.product} · Client: <b>{order?.client_name}</b>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit}>
          <div className="dispatch-field" style={{ marginBottom: 14 }}>
            <label className="dispatch-label" style={{ display: "block", fontWeight: 600, fontSize: 12.5, marginBottom: 6 }}>
              Transporter / Delivery Person <span style={{ color: "#dc2626" }}>*</span>
            </label>
            <input
              type="text"
              className="dispatch-control"
              value={transporter}
              onChange={(e) => setTransporter(e.target.value)}
              required
              placeholder="e.g. Suresh Kumar or Courier Name"
              style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid var(--line)", fontSize: 13 }}
              data-testid="out-transporter-input"
            />
          </div>

          <div className="dispatch-field" style={{ marginBottom: 14 }}>
            <label className="dispatch-label" style={{ display: "block", fontWeight: 600, fontSize: 12.5, marginBottom: 6 }}>
              Tracking / AWB Number (Optional)
            </label>
            <input
              type="text"
              className="dispatch-control"
              placeholder="e.g. TRK-98102"
              value={trackingNumber}
              onChange={(e) => setTrackingNumber(e.target.value)}
              style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid var(--line)", fontSize: 13 }}
              data-testid="out-tracking-input"
            />
          </div>

          <div className="dispatch-field" style={{ marginBottom: 18 }}>
            <label className="dispatch-label" style={{ display: "block", fontWeight: 600, fontSize: 12.5, marginBottom: 6 }}>
              Remarks (Optional)
            </label>
            <input
              type="text"
              className="dispatch-control"
              placeholder="e.g. Dispatched for afternoon delivery route"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid var(--line)", fontSize: 13 }}
            />
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button type="button" className="outline-btn" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button
              type="submit"
              className="primary-btn"
              disabled={busy || hasValidationError || !transporter.trim()}
              style={{ background: "#7c3aed", borderColor: "#7c3aed" }}
              data-testid="confirm-out-for-delivery-btn"
            >
              {busy ? "Processing…" : "Confirm & Send Out"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


